/**
 * SampleDataStep - Step 4a: Pick a sample dataset from 4 cards.
 */
import React, { useState } from 'react';
import {
  Box, Card, CardActionArea, Typography, Stack, Alert, useTheme, alpha, Chip, Link,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import ShoppingCartRounded from '@mui/icons-material/ShoppingCartRounded';
import CampaignRounded from '@mui/icons-material/CampaignRounded';
import LocalShippingRounded from '@mui/icons-material/LocalShippingRounded';
import ShieldRounded from '@mui/icons-material/ShieldRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import { brandPalette } from '../../../theme/brandPalette';
import OnboardingLayout from '../components/OnboardingLayout';
import type { DatasetId } from '../hooks/useOnboardingSession';

interface SampleDataStepProps {
  onSelect: (datasetId: DatasetId) => Promise<any>;
  /** Return to path choice; pass `own_data` so "Connect your own database" is pre-selected. */
  onBackToPathChoice?: (prefill: 'own_data') => void | Promise<void>;
  backNavLoading?: boolean;
  loading: boolean;
  error: string | null;
}

const DATASETS: Array<{
  id: DatasetId;
  Icon: React.ElementType;
  popular?: boolean;
  display_name: string;
  description: string;
  metrics: string;
  preview_question: string;
  example_questions: string[];
}> = [
  {
    id: 'insurance',
    Icon: ShieldRounded,
    popular: true,
    display_name: 'Insurance Distribution',
    description: 'Life & annuity funnel, placements, commissions, and lapse risk.',
    metrics: 'Conversion · CPA · Commissions · Lapse Risk',
    preview_question: 'Which policies are most likely to lapse?',
    example_questions: [
      "What's my lead-to-placed conversion by channel?",
      'Who are my top producers by commission?',
    ],
  },
  {
    id: 'ecommerce',
    Icon: ShoppingCartRounded,
    display_name: 'E-Commerce & Sales',
    description: 'Retail orders, customers, products, and revenue.',
    metrics: 'Revenue · Orders · Customers · Products',
    preview_question: 'Which product category is declining?',
    example_questions: [
      'Who are my top customers by revenue?',
      "What's my repeat purchase rate?",
    ],
  },
  {
    id: 'marketing',
    Icon: CampaignRounded,
    display_name: 'Marketing & Campaigns',
    description: 'Spend, leads, attribution, and conversion funnels.',
    metrics: 'CAC · Leads · Conversions · Spend',
    preview_question: 'Which channel has the best CAC?',
    example_questions: [
      'Why did conversion drop last month?',
      "What's my best performing campaign?",
    ],
  },
  {
    id: 'supply_chain',
    Icon: LocalShippingRounded,
    display_name: 'Supply Chain & Ops',
    description: 'Suppliers, fulfillment, and delivery performance.',
    metrics: 'On-Time % · Delays · Inventory · Suppliers',
    preview_question: 'Which supplier has the most delays?',
    example_questions: [
      "What's our on-time delivery rate?",
      'Where are our inventory gaps?',
    ],
  },
];

export default function SampleDataStep({
  onSelect,
  onBackToPathChoice,
  backNavLoading = false,
  loading,
  error,
}: SampleDataStepProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [selected, setSelected] = useState<DatasetId>('insurance');

  return (
    <OnboardingLayout maxWidth={900}>
      <Box sx={{ textAlign: 'center', mb: { xs: 2.5, md: 3.5 }, px: { xs: 0, sm: 0 } }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            mb: 1,
            letterSpacing: -0.4,
            fontSize: { xs: '1.125rem', sm: '1.35rem', md: '1.5rem' },
            lineHeight: 1.3,
            px: { xs: 0.5, sm: 0 },
          }}
        >
          What kind of data are you most familiar with?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' }, lineHeight: 1.5, maxWidth: 520, mx: 'auto' }}>
          We'll load a sample workspace so you can ask real questions straight away.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>{error}</Alert>}

      {/* md+: three cards in a row; xs–sm: stacked full-width for readable tap targets */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 2, md: 1.5 }}
        sx={{ mb: 3, alignItems: 'stretch' }}
      >
        {DATASETS.map((ds) => {
          const isSelected = selected === ds.id;
          return (
            <Card
              key={ds.id}
              sx={{
                flex: { xs: 'none', md: 1 },
                width: { xs: '100%', md: 'auto' },
                minWidth: 0,
                borderRadius: 2,
                border: '1px solid',
                borderColor: isSelected
                  ? alpha(brandPalette.primary[300], 0.65)
                  : isDark ? alpha('#fff', 0.1) : brandPalette.neutral[300],
                bgcolor: isSelected
                  ? (isDark ? alpha(brandPalette.primary[400], 0.06) : alpha(brandPalette.primary[100], 0.35))
                  : theme.palette.background.paper,
                transition: 'all 0.15s ease',
                boxShadow: isSelected
                  ? `0 1px 2px ${alpha(brandPalette.neutral[800], 0.06)}, 0 0 0 1px ${alpha(brandPalette.primary[300], 0.25)}`
                  : `0 1px 2px ${alpha(brandPalette.neutral[800], 0.04)}`,
                '&:hover': {
                  borderColor: isSelected ? alpha(brandPalette.primary[300], 0.85) : brandPalette.neutral[400],
                },
              }}
            >
              <CardActionArea onClick={() => setSelected(ds.id)} sx={{ p: 0, height: '100%', alignItems: 'flex-start' }}>
                <Box sx={{ p: { xs: 1.75, sm: 2 }, height: '100%', display: 'flex', flexDirection: 'column' }}>

                  {/* Icon row + checkmark */}
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
                    <Box sx={{
                      width: 40, height: 40, borderRadius: 1.5,
                      bgcolor: isSelected
                        ? alpha(brandPalette.primary[400], 0.2)
                        : isDark ? alpha('#fff', 0.06) : brandPalette.neutral[200],
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.15s',
                    }}>
                      <ds.Icon sx={{ fontSize: 20, color: isSelected ? brandPalette.primary[500] : brandPalette.neutral[600] }} />
                    </Box>
                    <CheckCircleRounded sx={{
                      fontSize: 20,
                      color: isSelected ? brandPalette.primary[400] : isDark ? alpha('#fff', 0.15) : brandPalette.neutral[400],
                      transition: 'color 0.15s',
                    }} />
                  </Stack>

                  {/* Popular badge */}
                  {ds.popular && (
                    <Box sx={{ mb: 0.75, display: 'inline-flex', alignSelf: 'flex-start' }}>
                      <Chip
                        label="Most popular"
                        size="small"
                        sx={{
                          height: 20, fontSize: '0.6rem', fontWeight: 600,
                          bgcolor: isSelected ? alpha(brandPalette.primary[300], 0.2) : brandPalette.neutral[200],
                          color: isSelected ? brandPalette.primary[500] : brandPalette.neutral[600],
                          border: `1px solid ${isSelected ? alpha(brandPalette.primary[300], 0.35) : brandPalette.neutral[300]}`,
                        }}
                      />
                    </Box>
                  )}

                  {/* Title + description */}
                  <Typography sx={{
                    fontWeight: 700,
                    fontSize: { xs: '0.9375rem', md: '0.88rem' },
                    lineHeight: 1.3,
                    mb: 0.5,
                    color: 'text.primary',
                  }}
                  >
                    {ds.display_name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.8125rem', md: '0.75rem' }, lineHeight: 1.45, mb: 1.25 }}
                  >
                    {ds.description}
                  </Typography>

                  {/* Metrics pills */}
                  <Typography sx={{
                    fontSize: { xs: '0.65rem', md: '0.62rem' },
                    fontWeight: 600,
                    color: isSelected ? brandPalette.neutral[700] : 'text.disabled',
                    letterSpacing: 0.2,
                    mb: 1.25,
                    transition: 'color 0.15s',
                  }}
                  >
                    {ds.metrics}
                  </Typography>

                  {/* All 3 questions - always visible */}
                  <Stack spacing={{ xs: 0.85, md: 0.75 }}>
                    {[ds.preview_question, ...ds.example_questions].map((q, qi) => (
                      <Box key={qi} sx={{
                        px: { xs: 1.1, md: 1.25 },
                        py: { xs: 0.85, md: 0.75 },
                        bgcolor: isSelected
                          ? alpha(brandPalette.neutral[200], 0.9)
                          : isDark ? alpha('#fff', 0.04) : brandPalette.neutral[200],
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: isSelected ? brandPalette.neutral[300] : 'transparent',
                        transition: 'all 0.15s',
                      }}>
                        <Typography sx={{
                          fontSize: { xs: '0.75rem', md: '0.67rem' },
                          color: isSelected ? brandPalette.neutral[700] : 'text.secondary',
                          fontStyle: 'italic',
                          lineHeight: 1.45,
                          transition: 'color 0.15s',
                        }}
                        >
                          "{q}"
                        </Typography>
                      </Box>
                    ))}
                  </Stack>

                </Box>
              </CardActionArea>
            </Card>
          );
        })}
      </Stack>

      <LoadingButton
        variant="contained"
        color="primary"
        fullWidth
        loading={loading}
        onClick={() => onSelect(selected)}
        size="large"
        endIcon={<ArrowForwardRounded />}
        sx={{
          py: { xs: 1.375, sm: 1.5 },
          minHeight: { xs: 48, sm: 'auto' },
          borderRadius: 2,
          fontWeight: 600,
          fontSize: { xs: '0.9375rem', sm: '1rem' },
          textTransform: 'none',
          bgcolor: brandPalette.primary[400],
          boxShadow: `0 1px 2px ${alpha(brandPalette.neutral[800], 0.08)}`,
          '&:hover': { bgcolor: brandPalette.primary[500] },
        }}
      >
        Explore {DATASETS.find(d => d.id === selected)?.display_name ?? 'dataset'}
      </LoadingButton>

      {onBackToPathChoice && (
        <Box sx={{ textAlign: 'center', mt: { xs: 2.5, sm: 2 } }}>
          <Link
            component="button"
            type="button"
            disabled={backNavLoading || loading}
            onClick={() => void onBackToPathChoice('own_data')}
            sx={{
              fontSize: { xs: '0.875rem', sm: '0.8125rem' },
              fontWeight: 600,
              cursor: backNavLoading || loading ? 'default' : 'pointer',
              border: 'none',
              background: 'none',
              py: 1,
              px: 0.5,
              fontFamily: 'inherit',
              textDecoration: 'underline',
              color: 'primary.main',
              display: 'inline-block',
              minHeight: 44,
              lineHeight: 1.3,
              '&:disabled': { color: 'text.disabled', textDecoration: 'none' },
            }}
          >
            Connect your own data instead →
          </Link>
        </Box>
      )}
    </OnboardingLayout>
  );
}
